export const CaseDifficulty = {
  easy: "Easy",
  medium: "Medium",
  hard: "Hard",
};

export const CaseMasterScreens = {
  HOME: 'HOME',
  CHALLENGES: 'CHALLENGES',
  PERSONALIZED: 'PERSONALIZED'
}

export const AttemptStates = {
  FEEDBACK_GENERATED: 'feedback_generated',
  FEEDBACK_SKIPPED: 'feedback_skipped',
  NOT_APPLICABLE: 'not_applicable'
};

// The five case filters, in the order they appear on the case list. `key` is the
// CasemasterTagType and the suffix of the `tags.<key>` query param.
export const CaseFacets = [
  { key: 'jobRole', label: 'Job Role', allLabel: 'All Roles' },
  { key: 'company', label: 'Company', allLabel: 'All Companies' },
  { key: 'domain', label: 'Domain', allLabel: 'All Domains' },
  { key: 'industry', label: 'Industry', allLabel: 'All Industries' },
  { key: 'specialization', label: 'Specialization', allLabel: 'All Specs' },
];

// Left hero banner rotation, per the PRD: a curated list, not every job-role
// tag. Written as they should read, so no case conversion is applied.
export const CaseHeroRoles = [
  'Consultant',
  'Product Management',
  'Entrepreneur in Residence',
  'Business Analyst',
  "Founder's Office",
  'Data Analyst',
];

// Right hero banner rotation, per the PRD. Each state owns the card that fronts
// the deck while it is showing - the deck holds one card per state and reorders
// itself as they rotate, so the artwork is listed here rather than keyed off
// `id` somewhere else.
export const CaseHeroStates = [
  {
    id: 'role',
    title: 'Role',
    description: 'Prepare for your specific job function',
    image: 'https://assets.languify.in/images/sheet-briefcase.png',
  },
  {
    id: 'domain',
    title: 'Domain',
    description: 'Master your functional area of expertise',
    image: 'https://assets.languify.in/images/sheet-domain.png',
  },
  {
    id: 'industry',
    title: 'Industry',
    description: 'Sector-specific challenges from Tech to FMCG',
    image: 'https://assets.languify.in/images/sheet-industry.png',
  },
  {
    id: 'company',
    title: 'Company',
    description: 'Crack the interview style of your dream firm',
    image: 'https://assets.languify.in/images/sheet-company.png',
  },
  {
    id: 'specialization',
    title: 'Degree Specialization',
    description: 'Bridge your academic major with business cases',
    image: 'https://assets.languify.in/images/sheet-degree.png',
  },
];

// Tag names are stored lowercase in casemasterTags, so anything displaying one
// title-cases it first. These are the words title-casing gets wrong: the first
// set is capitalised whole, the second carries capitals inside the word. Both
// match a whole word, case-insensitively - extend them as new tags appear.
const TAG_ACRONYMS = new Set([
  'bcg', 'ey', 'kpmg', 'tcs', 'iim', 'fms',
  'hr', 'it', 'gtm', 'fmcg', 'cpg', 'bfsi', 'nbfc', 'sme', 'qsr',
  'b2b', 'b2c', 'saas', 'ai', 'ml', 'roi', 'ebitda', 'pe', 'vc', 'ux', 'cx',
]);

const TAG_MIXED_CASE = {
  mckinsey: 'McKinsey',
  pwc: 'PwC',
};

/**
 * "google" -> "Google", "supply chain consultant" -> "Supply Chain Consultant",
 * "bcg" -> "BCG". The tail of each word is left as stored rather than
 * lowercased, so a name that already carries interior capitals keeps them.
 */
export const toTagLabel = (name) => String(name || '')
  .trim()
  .split(/\s+/)
  .filter(Boolean)
  .map((word) => {
    const lower = word.toLowerCase();

    if (TAG_MIXED_CASE[lower]) return TAG_MIXED_CASE[lower];
    if (TAG_ACRONYMS.has(lower)) return word.toUpperCase();

    return word.charAt(0).toUpperCase() + word.slice(1);
  })
  .join(' ');

/**
 * Every `tags.*` field is an array - a case is routinely tagged with several
 * job roles at once, four being common - and populate keeps it that way. A card
 * has room for one name per tag, so it takes the first. Written to accept a
 * bare object too, in case a caller hands over an unpopulated single reference.
 */
export const firstTagName = (tag) => {
  const value = Array.isArray(tag) ? tag[0] : tag;

  return typeof value?.name === 'string' ? value.name : '';
};

/**
 * metadata.casebook is hand-entered and inconsistent - "iim a (24-25)",
 * "iim b( 23- 24 )", "iim b", "fms consulting". It reads as an institute, the
 * section that follows it, and a batch in brackets. This pulls those apart and
 * returns them in one shape: "iim b( 23- 24 )" -> "IIM B (23-24)".
 *
 * `name` is the institute and section without the batch, for callers naming who
 * wrote the case rather than which book it came from. Only cases taken from a
 * real casebook carry the field at all - the generated ones have nothing here,
 * which is what makes its presence the mark of a non-generated case.
 */
export const parseCasebook = (casebook) => {
  const raw = String(casebook || '').trim();

  if (!raw) return { name: '', year: '', label: '' };

  const bracketed = raw.match(/\(([^)]*)\)/);
  const year = bracketed ? bracketed[1].replace(/\s+/g, '') : '';

  const name = toTagLabel(raw.replace(/\(.*?\)/g, ' '));

  return { name, year, label: year ? `${name} (${year})` : name };
};

// Sixth filter. Unlike the five above it isn't a tag - the backend resolves it
// against the case's attemptedBy list - so it carries its own fixed options and
// its query param is `attempted`, not `tags.attempted`.
export const CaseAttemptFacet = {
  key: 'attempted',
  label: 'Status',
  allLabel: 'All Cases',
  options: [
    { _id: 'true', name: 'Attempted' },
    { _id: 'false', name: 'Unattempted' },
  ],
};

export const AssessmentEntryPoint = {
  PERSONALIZED: 'personalized',
  GENERAL_CASE_STUDY: 'general_case_study',
  CONTEST: 'contest',
  MOCK: 'mock'
}
